import { spacing } from '@scality/core-ui';
import { Box } from '@scality/core-ui/dist/next';
import type React from 'react';
import { useContext } from 'react';
import styled from 'styled-components';
import { type ChatFeedEntry, isToolTrail } from '../../types/ClientMessage';
import { persistedValueToString } from '../../utils/persistedValue';
import { GuardianContext } from '../GuardianProvider';
import { ErrorMessageCard } from './ErrorMessageCard';
import { GuardianMarkdown } from './GuardianMarkdown';
import { InlineActionButtonGroup } from './InlineActionButtonGroup';
import { ToolActivity } from './ToolActivity';

interface ChatCardProps {
  response: ChatFeedEntry;
  isLastResponse: boolean;
  isThinking?: boolean;
  shouldAnimate: boolean;
}

export const BaseChatBox = styled.div`
  color: white;
  border-radius: ${spacing.f8};
  padding: ${spacing.f12} ${spacing.f24};
  white-space: pre-wrap;
  word-break: break-word;
  font-weight: 400;
  font-size: 1rem;
  /* Match GuardianMarkdown.MarkdownWrapper (1.3) so user (plain <span>) and
   * agent (markdown) bubbles share the same vertical rhythm. The previous
   * 32px applied to user bubbles only — agent content overrode it via the
   * markdown wrapper — and made multi-line user replies look much airier
   * than agent replies. */
  line-height: 1.3;
  & > p {
    margin: 0 0 ${spacing.f8} 0;
    &:nth-last-child(2) {
      margin-bottom: 0;
    }
  }
  & > pre {
    white-space: pre-wrap;
  }

  @media (max-width: 600px) {
    padding: ${spacing.f4} ${spacing.f12};
  }
`;

const OutgoingChatBox = styled(BaseChatBox)<{ shouldAnimate: boolean }>`
  background-color: ${(props) => props.theme.chatCardBlue};
  max-width: 66%;
  align-self: end;
  ${(props) => props.shouldAnimate && `animation: outgoingFadeInUpCard 0.5s ease-in-out;`}
`;

export const IncomingChatBox = styled(BaseChatBox)<{ shouldAnimate: boolean }>`
  background-color: ${(props) => props.theme.chatCardGray};
  width: calc(100% - ${spacing.f24} - ${spacing.f24});
  ${(props) => props.shouldAnimate && `animation: incomingFadeInUpCard 1s ease-in-out;`}

  @media (max-width: 600px) {
    width: calc(100% - ${spacing.f12} - ${spacing.f12});
  }
`;

/**
 * The row `scrollIntoView` targets. The chat scrolls *behind* the fixed navbar, so the
 * scroll margin has to clear it — and it has to sit on this element, the one holding the
 * ref: a scroll margin on an inner box is never read.
 */
const ChatCardRow = styled(Box)`
  scroll-margin-top: var(--header-height);
`;

export const ChatCard: React.FC<ChatCardProps> = ({ response, isLastResponse, isThinking = false, shouldAnimate }) => {
  const { lastChatCardRef } = useContext(GuardianContext);
  const isUserMessage = !isToolTrail(response) && response.role === 'user';
  const ChatBox = isUserMessage ? OutgoingChatBox : IncomingChatBox;

  return (
    <ChatCardRow
      ref={isLastResponse ? lastChatCardRef : null}
      display="flex"
      width="100%"
      justifyContent="right"
      {...(isLastResponse ? { 'aria-live': 'polite' } : {})}
    >
      <ChatBox shouldAnimate={shouldAnimate}>
        {isToolTrail(response) ? (
          <ToolActivity rows={response.rows} approval={response.approval} />
        ) : isUserMessage ? (
          <span>{persistedValueToString(response.content)}</span>
        ) : response.isError ? (
          <ErrorMessageCard message={persistedValueToString(response.content)} isLastResponse={isLastResponse} />
        ) : (
          <>
            <GuardianMarkdown
              md={persistedValueToString(response.content)}
              isLastResponse={isLastResponse}
              messageId={response.timestamp.toISOString()}
            />
            <InlineActionButtonGroup message={response} isThinking={isThinking} isLastResponse={isLastResponse} />
          </>
        )}
      </ChatBox>
    </ChatCardRow>
  );
};
